import graphql from "babel-plugin-relay/macro";
import { add, format } from "date-fns";
import { useFragment } from "react-relay";

import { useFacilityStartEndTime } from "@/features/Facility";
import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { InlineDatePicker } from "@whoosh/design-system/src/components/controls/Datepickers";
import { VStack } from "@whoosh/design-system/src/components/primitive";
import { useTodaysDate } from "@whoosh/utilities/src/dates";

import { TimeOfDayFilterControl } from "../../../../../../components/AgendaFilters";
import { useFacilityLessonBookingDays } from "../../../../../../features/Facility/hooks/useFacilityLessonBookingDays";
import { LessonTypeFilter } from "./LessonTypeFilter";
import { LessonsFiltersFragment_facility$key } from "./__generated__/LessonsFiltersFragment_facility.graphql";
import { LessonsFiltersFragment_session$key } from "./__generated__/LessonsFiltersFragment_session.graphql";

type Props = {
  sessionRef: LessonsFiltersFragment_session$key | null;
  facilityRef: LessonsFiltersFragment_facility$key | null;
  selectedLessonTypeId?: string;
  selectedDate?: string;
  selectedTimeOfDay?: { timeGTE?: string; timeLTE?: string };
  onLessonTypeFilterChange: (
    lessonTypeId: string,
    defaultDurationMins: number
  ) => void;
  onTimeOfDayFilterChange: (timeRange: {
    timeGTE?: string;
    timeLTE?: string;
  }) => void;
  onDateFilterChange: (date: string) => void;
};
export const LessonsFilters = ({
  sessionRef,
  facilityRef,
  selectedLessonTypeId,
  selectedTimeOfDay,
  selectedDate,
  onDateFilterChange,
  onTimeOfDayFilterChange,
  onLessonTypeFilterChange,
}: Props) => {
  const facility = useFragment(LessonsFiltersFragmentString, facilityRef);
  const session = useFragment(LessonsFiltersSessionFragmentString, sessionRef);
  const facilityStartEndTime = useFacilityStartEndTime(facility ?? null);
  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    facility?.club ?? null
  );

  const bookingWindowDays = useFacilityLessonBookingDays(facility ?? null);

  const endDate = format(
    add(new Date(), { days: bookingWindowDays }),
    "yyyy-MM-dd"
  );
  const startDate = useTodaysDate();

  const dateInfo = new Map<string, { selected: boolean }>();
  if (selectedDate) {
    dateInfo.set(selectedDate, { selected: true });
  }

  return (
    <VStack gap={8} px={2}>
      <LessonTypeFilter
        sessionRef={session ?? null}
        facilityRef={facility ?? null}
        onChange={onLessonTypeFilterChange}
        selectedLessonTypeId={selectedLessonTypeId}
      />
      <TimeOfDayFilterControl
        controlStartTime={facilityStartEndTime?.startTime}
        controlEndTime={facilityStartEndTime?.endTime}
        onChange={onTimeOfDayFilterChange}
        selectedTimeRange={selectedTimeOfDay}
      />
      <InlineDatePicker
        startDate={startDate}
        endDate={endDate}
        onSelectDate={onDateFilterChange}
        selectedDate={selectedDate}
        dateInfo={dateInfo}
        fillEmptyDays
        showDayOfWeekHeader
        clubWeekStartsOn={clubWeekStartsOn}
      />
    </VStack>
  );
};

const LessonsFiltersFragmentString = graphql`
  fragment LessonsFiltersFragment_facility on Facility {
    id
    ...useFacilityLessonBookingDays_facility
    ...LessonTypeFilter_facility
    ...DateSelectorControlFragment_facility
    ...useFacilityStartEndTime_facility
    club {
      ...useGetClubStartOfWeekNumberPatron_club
    }
  }
`;

const LessonsFiltersSessionFragmentString = graphql`
  fragment LessonsFiltersFragment_session on Session {
    ...LessonTypeFilter_session
  }
`;
